/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
} from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import {
  API_SERVER_HOST,
  API_SERVER_PORT,
  ENDPOINTS,
  TIMEOUT,
  AUTO_REFRESH_TARGET_STATUS_CODES,
} from '@constants/api-constants';
import { useAuthStore } from '@stores/auth-store';
import {
  camelKeysToSnakeKeys,
  snakeKeysToCamelKeys,
} from '@scripts/utils/object-utils';
import { camelToSnake } from '@scripts/utils/string-utils';
import {
  isArray,
  isFile,
  isFormData,
} from '@scripts/utils/type-utils';
import {
  QueryType,
  SignalType,
  PayloadType,
  UrlType,
  RequestConfigType,
  ResponseType,
  ValidateStatusType,
} from '@api/types';
import { refreshAccessToken } from '@api/auto-refresh';

export type GetArgsType = {
  url: UrlType;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type GetAllArgsType = {
  urls: Array<UrlType>;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type PostArgsType = {
  url: UrlType;
  payload: PayloadType;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type PutArgsType = {
  url: UrlType;
  payload: PayloadType;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type PutAllArgsType = {
  urls: Array<UrlType>;
  payloads: Array<PayloadType>;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type DeleteArgsType = {
  url: UrlType;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export type DeleteAllArgsType = {
  urls: Array<UrlType>;
  query?: QueryType;
  signal?: SignalType;
  validateStatus?: ValidateStatusType;
};

export class Client {
  client: AxiosInstance;
  useAuth: boolean;

  constructor({
    host = API_SERVER_HOST,
    port = API_SERVER_PORT,
    timeout = TIMEOUT,
    useAuth,
  }: {
    host?: string,
    port?: string,
    timeout?: number,
    useAuth: boolean,
  }) {
    this.useAuth = useAuth;

    this.client = axios.create({
      baseURL: `${host}:${port}`,
      timeout,
    });
    this.client.defaults.headers.common = {
      ...this.client.defaults.headers.common,
      'X-XSS-Protection': '1; mode=block',
    };

    if (useAuth) {
      this.setRequestInterceptor();
      this.setResponseInterceptor();
    }
  }

  setRequestInterceptor() {
    this.client.interceptors.request.use(
      (config) => {
        if (
          config.url
          && config.url.includes(ENDPOINTS.V1.REFRESH_TOKEN)
        ) {
          if (config?.headers instanceof AxiosHeaders) {
            config.headers.delete('Authorization');
          }
          return config;
        }

        const authStore = useAuthStore();
        const access = authStore.access || '';

        const headers = config.headers instanceof AxiosHeaders
          ? config.headers
          : new AxiosHeaders(config.headers);
        if (access) {
          headers.set('Authorization', `Bearer ${access}`);
        } else {
          headers.delete('Authorization');
        }

        return {
          ...config,
          headers,
        };
      },
      (error) => Promise.reject(error),
    );
  }

  setResponseInterceptor() {
    createAuthRefreshInterceptor(
      this.client,
      refreshAccessToken,
      {
        pauseInstanceWhileRefreshing: false,
        statusCodes: AUTO_REFRESH_TARGET_STATUS_CODES,
      },
    );
  }

  async get({
    url,
    query = {},
    signal = null,
    validateStatus = null,
  }: GetArgsType): Promise<ResponseType> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const res = await this.client.get(url as string, requestConfig);
    return Client.normalizeResponse(res);
  }

  async getAll({
    urls,
    query = {},
    signal = null,
    validateStatus = null,
  }: GetAllArgsType): Promise<ResponseType[]> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const requests: Promise<ResponseType>[] = (
      urls.map((url) => this.client.get(url, requestConfig))
    );
    const responses: ResponseType[] = await Promise.all(requests);
    return responses;
  }

  async post({
    url,
    payload,
    query = {},
    signal = null,
    validateStatus = null,
  }: PostArgsType): Promise<ResponseType> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const res = await this.client.post(
      url as string,
      Client.normalizePayload(payload),
      requestConfig,
    );
    return Client.normalizeResponse(res);
  }

  async put({
    url,
    payload,
    query = {},
    signal = null,
    validateStatus = null,
  }: PutArgsType): Promise<ResponseType> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const res = await this.client.put(
      url as string,
      Client.normalizePayload(payload),
      requestConfig,
    );
    return Client.normalizeResponse(res);
  }

  async putAll({
    urls,
    payloads,
    query = {},
    signal = null,
    validateStatus = null,
  }: PutAllArgsType): Promise<ResponseType[]> {
    if (urls.length !== payloads.length) {
      throw Error('Urls and payloads are different size.');
    }

    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const requests: Promise<ResponseType>[] = (
      urls.map((url, index) => this.client.put(
        url,
        Client.normalizePayload(payloads[index]),
        requestConfig,
      ))
    );
    const responses: ResponseType[] = await Promise.all(requests);
    return responses;
  }

  async delete({
    url,
    query = {},
    signal = null,
    validateStatus = null,
  }: DeleteArgsType): Promise<ResponseType> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const res = await this.client.delete(url as string, requestConfig);
    return Client.normalizeResponse(res);
  }

  async deleteAll({
    urls,
    query = {},
    signal = null,
    validateStatus = null,
  }: DeleteAllArgsType): Promise<ResponseType[]> {
    const requestConfig = Client.getRequestConfig({ query, signal, validateStatus });
    const requests: Promise<ResponseType>[] = (
      urls.map((url) => this.client.delete(url, requestConfig))
    );
    const responses: ResponseType[] = await Promise.all(requests);
    return responses;
  }

  static isValidBatchRequest({
    url,
    payload,
  }: {
    url: UrlType | Array<UrlType>;
    payload: PayloadType | Array<PayloadType>;
  }): boolean {
    const onlyUrlIsArray = isArray(url) && !isArray(payload);
    const invalidArraySize = (
      isArray(url)
      && isArray(payload)
      && url.length !== payload.length
    );
    const isInvalid = onlyUrlIsArray || invalidArraySize;
    return !isInvalid;
  }

  static isFileResponse(res: AxiosResponse): boolean {
    const contentDisposition = res.headers['content-disposition'];
    return contentDisposition && contentDisposition.indexOf('attachment') !== -1;
  }

  static normalizeFormData(formData: FormData): FormData {
    return Array.from(formData.entries()).reduce((result, [key, value]) => {
      const snakeKey = camelToSnake({ target: key });
      if (isFile(value)) {
        result.append(snakeKey, value, value.name);
      } else {
        result.append(snakeKey, value);
      }
      return result;
    }, new FormData());
  }

  static normalizePayload(payload: PayloadType | PayloadType[]) {
    if (isArray(payload)) {
      return payload.map((elem) => {
        if (isFormData(elem)) {
          return Client.normalizeFormData(elem);
        }
        return camelKeysToSnakeKeys({ target: elem });
      });
    }

    return isFormData(payload)
      ? Client.normalizeFormData(payload)
      : camelKeysToSnakeKeys({ target: payload });
  }

  static normalizeResponse(res: AxiosResponse): AxiosResponse {
    if (
      res.data === undefined
      || res.data === null
      || Client.isFileResponse(res)
    ) {
      return res;
    }

    return {
      ...res,
      data: snakeKeysToCamelKeys({ target: res.data }),
    };
  }

  static getRequestConfig({
    query,
    signal,
    validateStatus,
  }: {
    query: QueryType;
    signal: SignalType;
    validateStatus: ValidateStatusType;
  }): RequestConfigType {
    const target = camelKeysToSnakeKeys({ target: query });
    const params = new URLSearchParams();

    Object.entries(target).forEach(([key, value]) => {
      if (isArray(value)) {
        value.forEach((elem) => params.append(`${key}`, String(elem)));
      } else if (value != null) {
        params.append(key, String(value));
      }
    });

    return {
      params,
      signal,
      ...(!validateStatus ? {} : {
        validateStatus,
      }),
    } as RequestConfigType;
  }
}

export type ClientType = Client;

export type ClientsType = {
  readonly public: ClientType,
  readonly auth: ClientType,
};

export const clients: ClientsType = Object.freeze({
  public: new Client({ useAuth: false }),
  auth: new Client({ useAuth: true }),
});
